import { Button, Group, Popover, Stack, Text, Title } from '@mantine/core'
import { useDisclosure, useId } from '@mantine/hooks'
import { IconLock } from '@tabler/icons-react'
import { type FC } from 'react'

export interface VerificationDocumentLockButtonProps {
  documentTypeLabel: string
}

// Reusable lock + popover affordance for visible-but-not-downloadable PII documents.
// Built so future PII categories (banking docs, etc.) can pass a different
// `documentTypeLabel` without rewriting the component.
export const VerificationDocumentLockButton: FC<VerificationDocumentLockButtonProps> = ({ documentTypeLabel }) => {
  const [opened, { close, toggle }] = useDisclosure(false)
  const headingId = useId()

  return (
    <Popover
      closeOnClickOutside
      closeOnEscape
      onChange={(next) => {
        if (!next) close()
      }}
      opened={opened}
      position='left'
      returnFocus
      shadow='md'
      trapFocus
      width={320}
      withArrow
      withinPortal
      withRoles={false}
      zIndex={1003}
    >
      <Popover.Target>
        <Button
          aria-label={`Why can't I download this ${documentTypeLabel}?`}
          color='gray'
          compact
          h={44}
          leftIcon={<IconLock size={16} />}
          miw={44}
          onClick={toggle}
          size='xs'
          variant='subtle'
        >
          Locked
        </Button>
      </Popover.Target>
      <Popover.Dropdown
        aria-labelledby={headingId}
        role='dialog'
      >
        <Stack spacing='xs'>
          <Title
            id={headingId}
            order={4}
          >
            DPP has this document on file.
          </Title>
          <Text size='sm'>
            For your family&apos;s privacy, your {documentTypeLabel} stays in our secure system once you upload it — you
            can&apos;t download it back from this list.
          </Text>
          <Text size='sm'>
            If something on this document needs to change, just upload a new one. DPP will use the most recent version.
          </Text>
          <Text size='sm'>
            DPP staff can still see and download the original anytime through their internal system.
          </Text>
          <Group position='right'>
            <Button
              autoFocus
              color='brand-red'
              mt='xs'
              onClick={close}
              size='xs'
            >
              Got it
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
